import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate, inr, localIsoDate } from "@/lib/utils";
import {
  Loader2, ChevronLeft, ChevronRight, Check, IndianRupee,
} from "lucide-react";

/**
 * iter61 — Weekly Payroll table, extracted from `/team` PayrollTab and moved
 * into the standalone `/financials` workspace (admin-only).
 *
 * Behavior unchanged from iter59:
 *   • Sun→Sat payroll cycle (iter35).
 *   • Bulk "Mark All Paid" fires POST /team/payroll/mark-paid per pending row
 *     in parallel — endpoint is idempotent on (staff_id, week_start).
 *   • Zero-gross rows can't be marked paid.
 */
function ymd(d) { return localIsoDate(d); }
function sundayOf(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; }

export default function WeeklyPayroll() {
  const [anchor, setAnchor] = useState(() => sundayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/payroll?week_of=${ymd(anchor)}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [anchor]);
  useEffect(() => { load(); }, [load]);

  const shiftWeek = (n) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + 7 * n);
    setAnchor(d);
  };

  const markPaid = async (r) => {
    if (!(r.gross_amount > 0)) return;
    if (!window.confirm(`Mark ${r.name}'s ${inr(r.gross_amount)} as paid for week starting ${data.week_start}?`)) return;
    await api.post("/team/payroll/mark-paid", {
      staff_id: r.staff_id, week_start: data.week_start, amount: r.gross_amount,
    });
    load();
  };

  const pendingRows = (data?.rows || []).filter((r) => !r.paid && r.gross_amount > 0);
  const canBulk = pendingRows.length > 0 && !bulkBusy;

  const markAllPaid = async () => {
    if (!canBulk) return;
    const totalToPay = pendingRows.reduce((s, r) => s + Number(r.gross_amount || 0), 0);
    const msg = `Mark ${pendingRows.length} pending payment${pendingRows.length === 1 ? "" : "s"} totalling ${inr(totalToPay)} as paid for week starting ${fmtDate(data.week_start)}?`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      await Promise.all(
        pendingRows.map((r) =>
          api.post("/team/payroll/mark-paid", { staff_id: r.staff_id, week_start: data.week_start, amount: r.gross_amount })
        )
      );
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="payroll-tab">
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <button onClick={() => shiftWeek(-1)} className="btn btn-outline btn-sm" data-testid="payroll-prev-week">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <div className="text-sm">
          <div className="font-heading font-semibold text-stone-900">
            Week of <span data-testid="payroll-week-label">{data?.week_start ? fmtDate(data.week_start) : "…"}</span>
          </div>
          <div className="text-[11px] text-stone-500 font-mono">{data?.week_start} → {data?.week_end}</div>
        </div>
        <button onClick={() => shiftWeek(1)} className="btn btn-outline btn-sm" data-testid="payroll-next-week">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-sm">
            <span className="text-stone-500">Gross</span>{" "}
            <span className="font-heading text-lg font-semibold text-stone-900" data-testid="payroll-gross-total">
              {data ? inr(data.totals.gross_amount) : "—"}
            </span>
            <span className="text-stone-400 ml-2 text-xs" data-testid="payroll-paid-total">
              Paid {data ? inr(data.totals.paid_amount) : "—"}
            </span>
          </div>
          <button
            type="button"
            onClick={markAllPaid}
            disabled={!canBulk}
            className={`btn btn-primary btn-sm ${!canBulk ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="payroll-mark-all-paid"
            title={pendingRows.length === 0 ? "No pending payments in this week" : `Mark ${pendingRows.length} pending payments as paid`}
          >
            {bulkBusy
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Marking…</>
              : <><Check className="w-3.5 h-3.5" strokeWidth={2.4} /> Mark All Paid{pendingRows.length > 0 ? ` · ${pendingRows.length}` : ""}</>}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading…
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Staff</th>
                <th className="text-right px-3 py-2 font-semibold">Rate</th>
                <th className="text-right px-3 py-2 font-semibold">Days worked</th>
                <th className="text-right px-3 py-2 font-semibold">Holidays</th>
                <th className="text-right px-3 py-2 font-semibold">Gross</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data?.rows?.map((r) => (
                <tr key={r.staff_id} data-testid={`payroll-row-${r.staff_id}`}>
                  <td className="px-3 py-2 font-semibold text-stone-800">{r.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(r.per_day_rate)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.days_present}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-700">{r.days_holiday}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`payroll-gross-${r.staff_id}`}>
                    <IndianRupee className="w-3 h-3 inline -mt-0.5" strokeWidth={2} />{Number(r.gross_amount).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2">
                    {r.paid
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"><Check className="w-3 h-3" strokeWidth={2.4} /> Paid</span>
                      : <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200">Pending</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!r.paid && r.gross_amount > 0 && (
                      <button className="btn btn-primary btn-sm" onClick={() => markPaid(r)} data-testid={`payroll-mark-paid-${r.staff_id}`}>
                        <Check className="w-3 h-3" strokeWidth={2.4} /> Mark paid
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!data || data.rows.length === 0) && (
                <tr><td colSpan={7} className="text-center py-10 text-stone-400 text-sm">No active staff.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
